import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Trash2 } from "lucide-react";
import { useAccount, photoUrl, errMsg } from "@/lib/account";

export const AdminStories = () => {
  const { authed } = useAccount();
  const [items, setItems] = useState([]);
  const load = useCallback(() => authed("get", "/admin/stories").then(setItems).catch(() => {}), [authed]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!items.length) return <p className="text-slate-500 py-10 text-center" data-testid="admin-stories-empty">No stories submitted yet.</p>;
  return items.map((s) => (
    <div key={s.id} className="card-royal rounded-2xl p-5 flex gap-4" data-testid={`admin-story-${s.id}`}>
      {s.photo && <img src={photoUrl(s.photo)} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl">{s.couple} <span className="text-xs uppercase tracking-widest text-slate-400 ml-2">{s.status}</span></p>
        <p className="text-xs text-slate-400">{[s.country, s.year].filter(Boolean).join(" · ")}</p>
        <p className="text-sm text-slate-300 mt-1 line-clamp-3">{s.story}</p>
      </div>
      <div className="flex flex-col gap-2">
        {s.status !== "approved" && <button onClick={() => act(() => authed("post", `/admin/stories/${s.id}/approve`), "Published")} className="btn-gold p-2 rounded-full" data-testid={`admin-story-approve-${s.id}`}><Check className="w-4 h-4" /></button>}
        <button onClick={() => window.confirm("Delete this story?") && act(() => authed("delete", `/admin/stories/${s.id}`), "Deleted")} className="p-2 rounded-full border border-red-500/40 text-red-300" data-testid={`admin-story-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  ));
};

export const AdminPayments = () => {
  const { authed } = useAccount();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => authed("get", "/admin/payments").then(setD).catch(() => {}), [authed]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const check = async () => {
    setBusy(true);
    try { const r = await authed("post", "/admin/payments/reconcile"); toast.success(`Checked with Razorpay — ${r.recovered} payment(s) recovered`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (!d) return null;
  const Row = ({ p, paid }) => (
    <div className="p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`admin-payment-${p.order_id}`}>
      <span className="flex-1 min-w-[180px] truncate">{p.email || p.user_id}</span>
      <span className="text-slate-400">{p.kind === "plan" ? `${p.plan_id} plan` : "Top-up"}</span>
      <span className="text-slate-500 text-xs">{p.order_id}</span>
      <span className="text-slate-400">{new Date(paid ? p.paid_at : p.created_at).toLocaleString()}</span>
      <span className={paid ? "text-emerald-300 font-semibold" : "text-amber-300"}>₹{p.amount}</span>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div className="flex flex-wrap items-center gap-4">
        <span data-testid="admin-payments-mode" className={`px-3 py-1 rounded-full text-xs font-semibold ${d.live_mode ? "bg-emerald-900/70 text-emerald-300" : "bg-amber-900/60 text-amber-200"}`}>{d.live_mode ? "LIVE payments" : "TEST mode — no real money"}</span>
        <span className="font-display text-3xl text-amber-200" data-testid="admin-payments-today-total">₹{d.today_total}</span>
        <span className="text-sm text-slate-400">received today ({d.today.length})</span>
        <button onClick={check} disabled={busy} className="btn-ghost px-4 py-2 rounded-full text-sm ml-auto" data-testid="admin-payments-reconcile">{busy ? "Checking..." : "Check pending with Razorpay"}</button>
      </div>
      <div className="card-royal rounded-2xl divide-y divide-slate-800"><p className="p-4 text-xs uppercase tracking-widest text-slate-400">Paid today</p>
        {d.today.length ? d.today.map((p) => <Row key={p.order_id} p={p} paid />) : <p className="p-4 text-sm text-slate-500">No payments yet today.</p>}</div>
      <div className="card-royal rounded-2xl divide-y divide-slate-800"><p className="p-4 text-xs uppercase tracking-widest text-slate-400">Waiting for confirmation (last 3 days)</p>
        {d.pending.length ? d.pending.map((p) => <Row key={p.order_id} p={p} />) : <p className="p-4 text-sm text-slate-500">Nothing pending.</p>}</div>
      <p className="text-xs text-slate-500">Pending orders include checkouts people started but didn't finish. Paid ones are confirmed automatically every 5 minutes.</p>
    </div>
  );
};

const EMPTY = { title: "", percent: 20, start: "", end: "", text: "", active: true };

export const AdminOffers = () => {
  const { authed } = useAccount();
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => authed("get", "/admin/offers").then(setItems).catch(() => {}), [authed]);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, percent: Number(f.percent) };
      await (editId ? authed("put", `/admin/offers/${editId}`, body) : authed("post", "/admin/offers", body));
      toast.success("Offer saved"); setF(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { try { await authed("delete", `/admin/offers/${id}`); load(); } catch (err) { toast.error(errMsg(err)); } };
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="space-y-4" data-testid="admin-offers">
      <form onSubmit={save} className="card-royal rounded-2xl p-5 grid sm:grid-cols-6 gap-3" data-testid="offer-form">
        <input className="field sm:col-span-2" placeholder="Title (e.g. Diwali Dhamaka)" required value={f.title} onChange={on("title")} data-testid="offer-title-input" />
        <input className="field" type="number" min={1} max={90} value={f.percent} onChange={on("percent")} data-testid="offer-percent-input" />
        <input className="field" type="date" required value={f.start.slice(0, 10)} onChange={on("start")} data-testid="offer-start-input" />
        <input className="field" type="date" required value={f.end.slice(0, 10)} onChange={on("end")} data-testid="offer-end-input" />
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.active} onChange={on("active")} className="accent-amber-500" data-testid="offer-active-checkbox" /> Active</label>
        <input className="field sm:col-span-5" placeholder="Banner text" maxLength={140} value={f.text} onChange={on("text")} data-testid="offer-text-input" />
        <button className="btn-gold rounded-full py-2" data-testid="offer-save-button">{editId ? "Update" : "Add offer"}</button>
      </form>
      {items.map((o) => (
        <div key={o.id} className="card-royal rounded-2xl p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`admin-offer-${o.id}`}>
          <span className="font-display text-xl flex-1 min-w-[160px]">{o.title}</span>
          <span className="text-amber-300 font-semibold">{o.percent}% off</span>
          <span className="text-slate-400">{o.start.slice(0, 10)} → {o.end.slice(0, 10)}</span>
          <span className={o.active ? "text-emerald-300" : "text-slate-500"}>{o.active ? "active" : "paused"}</span>
          <button onClick={() => { setEditId(o.id); setF({ ...o }); }} className="btn-ghost px-3 py-1 rounded-full" data-testid={`admin-offer-edit-${o.id}`}>Edit</button>
          <button onClick={() => del(o.id)} className="p-2 rounded-full border border-red-500/40 text-red-300" data-testid={`admin-offer-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
};
